/**
 * showToast, the app's notification API.
 *
 * The IMPLEMENTATION changed to the design system's `Toast`; the API did not.
 * Every call site already goes through this one module, which is why replacing
 * react-toastify is a two-file change rather than a sweep of every page.
 * `showToast.success(...)` and its three siblings keep exactly the signature
 * they have always had, so nothing that calls them needs editing.
 *
 * WHY A MODULE-LEVEL BRIDGE
 * -------------------------
 * The design system exposes toasts as a hook, `useToast()`, which is the right
 * shape for a component. But `showToast` is imported as a module singleton and
 * called from async handlers, catch blocks and container callbacks, some of
 * which are not rendering when they fire. A hook cannot be called from there.
 *
 * So `ToastHost` registers the provider's imperative API here on mount and this
 * module forwards to it. One indirection, no call-site churn, and the design
 * system stays the single owner of how a toast looks and behaves.
 *
 * BEFORE THE HOST MOUNTS, calls are QUEUED rather than dropped. A notification
 * that vanishes because of a mount-order race is the failure mode that hides a
 * broken save from the user, which is far worse than one arriving a frame late.
 */

import { errorText, GENERIC_FAILURE } from './errorText';

let handle = null;
const queue = [];

/** Called by ToastHost. Draining on registration is what makes the queue work. */
export function registerToastHandle(api) {
  handle = api;
  while (queue.length) {
    const next = queue.shift();
    handle[next.severity]({ title: next.title, duration: next.duration });
  }
  return () => {
    handle = null;
  };
}

/**
 * Turns whatever a caller passed into something a person can read.
 *
 * Two real cases, both seen in this app:
 *
 * 1. An ERROR OBJECT. react-toastify printed "[object Object]".
 * 2. A WHOLE HTML DOCUMENT. When the API is unreachable the dev proxy answers
 *    with its own HTML error page, and the axios error carries that markup as
 *    a string. Verified live: submitting /forget-password produced a toast
 *    reading `<!DOCTYPE html> <html lang="en"> <head> <title>Error</tit`.
 *    Anything markup shaped is therefore refused in favour of a sentence,
 *    because a stack of angle brackets tells the user nothing and looks broken.
 *
 * The rule itself now lives in `utils/errorText.js`, because empty states had
 * exactly the same two problems and were solving them nowhere. One rule, one
 * place, so a toast and an empty state cannot drift apart on what "unreadable"
 * means. Only the fallback SENTENCE differs, and it differs on purpose: a
 * toast already implies the action that failed, a table does not.
 */
const readable = (message) => errorText(message, GENERIC_FAILURE);

function emit(severity, message, duration) {
  const title = readable(message);

  if (!handle) {
    queue.push({ severity, title, duration });
    return;
  }
  handle[severity]({ title, duration });
}

// These durations are the ones react-toastify was configured with, kept so the
// change is invisible to anyone using the app. Errors linger longest, because
// they are the ones a user needs time to read and act on.
export const showToast = {
  success: (message) => emit('success', message, 3000),
  error: (message) => emit('error', message, 4000),
  info: (message) => emit('info', message, 3000),
  warning: (message) => emit('warning', message, 3500),
};

export default showToast;
